import { supabase } from "../lib/supabase";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  image?: string;
};

// --- Auth Helpers ---

async function getSession() {
  const { data } = await supabase.auth.getSession();
  if (!data.session?.user) return { data: null };

  const user = data.session.user;
  return {
    data: {
      user: {
        id: user.id,
        email: user.email || "",
        name: user.user_metadata?.full_name || user.email?.split("@")[0] || "Collector",
        image: user.user_metadata?.avatar_url,
      } as AuthUser
    }
  };
}

function renderAuthUI(
  container: HTMLElement,
  options?: { onLogin?: (user: AuthUser) => void; redirectTo?: string }
) {
  // Simple bespoke UI that uses Supabase magic link or password
  container.innerHTML = `
    <div class="bg-slate-900 border border-white/10 rounded-2xl p-6 text-white">
      <h3 class="text-lg font-bold mb-2">Sign in to PlayFair</h3>
      <p class="text-slate-400 text-sm mb-4">Enter your email to receive a login link.</p>
      <form id="playfair-auth-form" class="space-y-3">
        <input id="playfair-auth-input" type="email" required placeholder="you@example.com"
          class="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2" />
        <button type="submit" class="w-full bg-white text-black font-semibold rounded-xl py-2">Send Magic Link</button>
      </form>
      <div id="auth-message" class="mt-4 text-center text-sm hidden"></div>
    </div>
  `;

  const form = container.querySelector("#playfair-auth-form") as HTMLFormElement;
  const input = container.querySelector("#playfair-auth-input") as HTMLInputElement;
  const msg = container.querySelector("#auth-message") as HTMLElement;

  if (!form) return;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = input.value.trim();
    if (!email) return;

    msg.textContent = "Sending...";
    msg.classList.remove("hidden");

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: options?.redirectTo || window.location.href },
    });

    if (error) {
      msg.textContent = error.message;
      msg.classList.add("text-red-400");
    } else {
      msg.textContent = "Check your email for the login link!";
      msg.classList.add("text-green-400");
      input.value = "";
    }
  });
}

async function signOut() {
  await supabase.auth.signOut();
  window.location.reload();
}

// --- API Helpers ---

// Legacy compatibility wrapper for direct DB calls (if any components use this)
// Ideally components should use supabase directly, but we keep this for now.
async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  // This is a stub. In a full migration, we'd replace calls to this with supabase.from(...)
  console.warn("Using legacy apiFetch for:", path);
  return fetch(path, init);
}

// Proxy Ximilar calls to Supabase Edge Functions
export async function ximilarFetch(path: string, init?: RequestInit): Promise<Response> {
  // Extract function name from path (e.g., /api/identify -> identify)
  const endpoint = path.replace("/api/", "").replace(/^\//, "");

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Use anon key only — this Edge Function doesn't need user auth
  const functionUrl = `${supabaseUrl}/functions/v1/ximilar-proxy/${endpoint}`;

  const response = await fetch(functionUrl, {
    method: init?.method || 'POST',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${anonKey}`,
      "apikey": anonKey,
    },
    body: init?.body,
  });

  return response;
}

export const client = {
  api: {
    fetch: apiFetch,
  },
  auth: {
    getSession,
    renderAuthUI,
    signOut,
    signInWithOtp: (params: any) => supabase.auth.signInWithOtp(params),
    forgetPassword: {
      async emailOtp({ email }: { email: string }) {
        await supabase.auth.resetPasswordForEmail(email);
        return { success: true };
      },
    }
  },
};
